import { ReactNode, CSSProperties, useState, useEffect } from 'react';
import {
  Control,
  useDropdown,
  ControlProps,
  ClearableFooter,
  MenuItem,
  Menu,
} from '@adsk/alloy-react-dropdown';
import Portal from '@adsk/alloy-react-portal';
import type { GenericRecord } from '../../types';
import { EMPTY_OBJECT } from '../../constants';

export type SingleSelectDropdownProps<T extends GenericRecord> = {
  id?: string;
  options: Array<T>;
  value?: T['value'] | null;
  style?: CSSProperties;
  placeholder?: string;
  onChange: (selectedElement: T['value']) => void;
  renderItem?: ControlProps<T>['renderItem'];
  ['data-testid']?: string;
  disabled?: boolean;
  clearable?: boolean;
  renderLeftComponent?: (item: T) => ReactNode;
  error?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
};

export const SingleSelectDropdown = <T extends GenericRecord>({
  id,
  options,
  value = null,
  onChange,
  placeholder = '',
  style = {},
  renderItem,
  'data-testid': dataTestId,
  disabled,
  clearable,
  renderLeftComponent,
  error,
  isOpen: isOpenProp = false,
  onClose,
}: SingleSelectDropdownProps<T>) => {
  const [isMenuOpen, setIsMenuOpen] = useState(isOpenProp);
  useEffect(() => {
    setIsMenuOpen(isMenuOpen);
  }, [isMenuOpen]);

  const controlledSelectedItem = options.find((option) => option.value === value) || null;

  const {
    isOpen,
    menuItems,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getItemProps,
    selectedItem,
    highlightedIndex,
    targetProps,
  } = useDropdown({
    items: options,
    selectedItem: controlledSelectedItem,
    onSelectedItemChange: (changes) => {
      onChange(changes.selectedItem?.value as T['value']);
    },
    id,
    isOpen: isMenuOpen,
    onIsOpenChange: ({ isOpen: _isOpen = false }) => {
      if (_isOpen === false) {
        onClose?.();
      }

      setIsMenuOpen(_isOpen);
    },
  });

  return (
    <div style={{ width: '100%', ...style }}>
      <Control
        isOpen={isOpen}
        disabled={disabled}
        selectedItem={selectedItem}
        getToggleButtonProps={getToggleButtonProps}
        getLabelProps={getLabelProps}
        targetProps={targetProps}
        placeholder={placeholder}
        renderItem={renderItem}
        data-testid={dataTestId}
        error={error}
      />
      <Portal container={document.body}>
        <Menu
          isOpen={isOpen}
          targetProps={targetProps}
          getMenuProps={getMenuProps}
          {...(clearable && {
            renderFooter: () => (
              <ClearableFooter setSelectedItems={() => onChange('')} style={EMPTY_OBJECT} />
            ),
          })}
        >
          {menuItems.map((item, index) => {
            const leftComponent = () => renderLeftComponent && renderLeftComponent(item);
            return (
              <MenuItem
                key={item.value}
                {...getItemProps?.({
                  disabled: item.disabled,
                  item,
                  index,
                })}
                isSelected={selectedItem?.value === item.value}
                isHighlighted={highlightedIndex === index}
                disabled={item.disabled}
                text={item.label}
                subtext={item.subtext}
                renderLeftComponent={leftComponent}
              />
            );
          })}
        </Menu>
      </Portal>
    </div>
  );
};
